import RenderPipeline from "../../../lib/services/render/renderpipeline";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import ColorCameraSlot from "../../../lib/services/capture/color_camera_slot";

export default class DebugBlobOverlayPipeline extends RenderPipeline {
  label: string;
  shader_name: string;

  sampler: GPUSampler | null = null;
  bind_group: GPUBindGroup | null = null;
  params_buffer: GPUBuffer | null = null;
  private layout: GPUBindGroupLayout | null = null;

  constructor(key: string, shader_name: string) {
    super(key + "_debug_blob");
    this.label = key + "_debug_blob";
    this.shader_name = shader_name;
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    _resource_service: ResourceService
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

    this.params_buffer = device.createBuffer({
      label: this.label + "_params",
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.sampler = device.createSampler({
      label: this.label + "_sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    });

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: { module: shader, entryPoint: "vs_main", buffers: [] },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [{
          format,
          blend: {
            color: { operation: "add", srcFactor: "one", dstFactor: "zero" },
            alpha: { operation: "add", srcFactor: "one", dstFactor: "zero" },
          },
        }],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: false,
        depthCompare: "always",
      },
    });

    this.layout = this.pipeline.getBindGroupLayout(0);
  }

  update_params(
    device: GPUDevice,
    mode: number,
    src_width: number,
    src_height: number,
    dst_width: number,
    dst_height: number
  ): void {
    if (!this.params_buffer) return;
    const buf = new ArrayBuffer(32);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    u32[0] = mode;
    f32[4] = src_width;
    f32[5] = src_height;
    f32[6] = dst_width;
    f32[7] = dst_height;
    device.queue.writeBuffer(this.params_buffer, 0, buf);
  }

    bind_textures(
    device: GPUDevice,
    cam_slot: ColorCameraSlot,
    blob_view: GPUTextureView
  ): void {
    if (!this.pipeline || !this.sampler || !this.params_buffer || !this.layout) return;

    const entries: GPUBindGroupEntry[] = [
      { binding: 0, resource: { buffer: this.params_buffer } },
      { binding: 1, resource: this.sampler },
      { binding: 2, resource: cam_slot.y_view! },
    ];

    if (cam_slot.format === "NV12") {
      entries.push({ binding: 3, resource: cam_slot.cbcr_view! });
      entries.push({ binding: 4, resource: blob_view });
    } else {
      entries.push({ binding: 3, resource: cam_slot.u_view! });
      entries.push({ binding: 4, resource: cam_slot.v_view! });
      entries.push({ binding: 5, resource: blob_view });
    }

    this.bind_group = device.createBindGroup({
      layout: this.layout,
      label: this.label + "_bind_group",
      entries,
    });
  }

  prepare(_device: GPUDevice): void {}

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group) return;
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.draw(6);
  }
}
